// Create / edit form for a platform user: profile (email is immutable once
// created), password, roles, extra direct permissions and company access.
// Controlled by `UsersView`; the dialog owns only its draft state and submits
// through `apiClient`, surfacing server-side validation errors inline.

import { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import type {
  Company,
  LanguageCode,
  PermissionCode,
  PermissionInfo,
  RoleInfo,
  UserAccount,
} from '../domain';
import { PASSWORD_MIN, USER_NAME_MAX, isSuperAdmin, isValidEmail } from '../domain';
import { createUser, updateUser } from '../data/apiClient';
import { LOCALES, LOCALE_META } from '../i18n/messages';
import { useTranslation } from '../state/I18nContext';

// Prefer the locale catalogue entry for a role/permission; unknown (custom)
// entries fall back to the server-provided English text.
function catalogText(
  t: (key: string) => string,
  key: string,
  fallback: string,
): string {
  const translated = t(key);
  return translated === key ? fallback : translated;
}

interface UserDialogProps {
  open: boolean;
  /** The user being edited, or null to create a new one. */
  user: UserAccount | null;
  roles: RoleInfo[];
  permissions: PermissionInfo[];
  companies: Company[];
  /** Companies pre-linked when creating (e.g. the open company profile's). */
  initialCompanyIds?: number[];
  onClose: () => void;
  /** Called after a successful save so the parent can refresh its lists. */
  onSaved: (saved: UserAccount) => void;
}

export function UserDialog({
  open,
  user,
  roles,
  permissions,
  companies,
  initialCompanyIds,
  onClose,
  onSaved,
}: UserDialogProps) {
  const { t } = useTranslation();
  const creating = user === null;

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  // '' means "follow the company default" (stored as null server-side).
  const [language, setLanguage] = useState<LanguageCode | ''>('');
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [permissionIds, setPermissionIds] = useState<PermissionCode[]>([]);
  const [companyIds, setCompanyIds] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed the draft each time the dialog opens (for a different user, or
  // fresh for creation).
  useEffect(() => {
    if (!open) return;
    setEmail(user?.email ?? '');
    setName(user?.name ?? '');
    setPassword('');
    setLanguage(user?.language ?? '');
    setRoleIds(user?.roleIds ?? []);
    setPermissionIds(user?.permissionIds ?? []);
    setCompanyIds(user?.companyIds ?? initialCompanyIds ?? []);
    setError(null);
  }, [open, user, initialCompanyIds]);

  const superAdminSelected = useMemo(() => isSuperAdmin({ roleIds }), [roleIds]);

  // The company picker only offers companies the manager can see. A user may
  // already be linked to companies outside that set (e.g. a company-scoped
  // manager editing a user who also belongs to companies they can't access);
  // those links are kept untouched — only the visible selection is editable.
  const companyById = useMemo(
    () => new Map(companies.map((company) => [company.id, company])),
    [companies],
  );
  const selectedCompanies = useMemo(
    () =>
      companyIds
        .map((id) => companyById.get(id))
        .filter((company): company is Company => company !== undefined),
    [companyIds, companyById],
  );
  const setSelectedCompanies = (next: Company[]) => {
    const visibleIds = new Set(companies.map((company) => company.id));
    const hiddenIds = companyIds.filter((id) => !visibleIds.has(id));
    setCompanyIds([...hiddenIds, ...next.map((company) => company.id)]);
  };

  const emailValid = isValidEmail(email);
  const passwordValid = creating
    ? password.length >= PASSWORD_MIN
    : password.length === 0 || password.length >= PASSWORD_MIN;
  const canSubmit = !busy && emailValid && passwordValid;

  const toggle = <T,>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const saved = creating
        ? await createUser({
            email,
            name,
            password,
            language: language === '' ? null : language,
            roleIds,
            permissionIds,
            companyIds,
          })
        : await updateUser(user.id, {
            name,
            ...(password.length > 0 ? { password } : {}),
            language: language === '' ? null : language,
            roleIds,
            permissionIds,
            companyIds,
          });
      onSaved(saved);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.users.errors.save'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {creating
          ? t('admin.users.newUser')
          : t('admin.users.editUser', { email: user.email })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t('admin.users.dialog.emailLabel')}
            fullWidth
            autoFocus={creating}
            value={email}
            disabled={!creating}
            onChange={(e) => setEmail(e.target.value)}
            error={creating && email.length > 0 && !emailValid}
            helperText={
              creating
                ? email.length > 0 && !emailValid
                  ? t('admin.users.dialog.emailInvalid')
                  : t('admin.users.dialog.emailHelp')
                : t('admin.users.dialog.emailImmutable')
            }
          />
          <TextField
            label={t('admin.users.dialog.nameLabel')}
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            slotProps={{ htmlInput: { maxLength: USER_NAME_MAX } }}
          />
          <TextField
            label={
              creating
                ? t('admin.users.dialog.passwordLabel')
                : t('admin.users.dialog.newPasswordLabel')
            }
            type="password"
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={password.length > 0 && !passwordValid}
            helperText={
              creating
                ? t('admin.users.dialog.passwordMin', { count: PASSWORD_MIN })
                : t('admin.users.dialog.passwordKeep', { count: PASSWORD_MIN })
            }
          />

          <FormControl fullWidth>
            <InputLabel id="user-language-label">
              {t('admin.users.dialog.languageLabel')}
            </InputLabel>
            <Select
              labelId="user-language-label"
              label={t('admin.users.dialog.languageLabel')}
              value={language}
              onChange={(e) => setLanguage(e.target.value as LanguageCode | '')}
            >
              <MenuItem value="">
                {t('admin.users.dialog.languageDefault')}
              </MenuItem>
              {LOCALES.map((code) => (
                <MenuItem key={code} value={code}>
                  {LOCALE_META[code].label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider />

          <Typography variant="subtitle2">{t('admin.users.dialog.roles')}</Typography>
          <FormGroup>
            {roles.map((role) => (
              <FormControlLabel
                key={role.id}
                control={
                  <Checkbox
                    checked={roleIds.includes(role.id)}
                    onChange={() => setRoleIds((prev) => toggle(prev, role.id))}
                  />
                }
                label={
                  <span>
                    {catalogText(t, `admin.access.roles.${role.id}.name`, role.name)}
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block' }}
                    >
                      {catalogText(
                        t,
                        `admin.access.roles.${role.id}.description`,
                        role.description,
                      )}
                    </Typography>
                  </span>
                }
              />
            ))}
          </FormGroup>

          <Typography variant="subtitle2">
            {t('admin.users.dialog.extraPermissions')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('admin.users.dialog.extraPermissionsHelp')}
          </Typography>
          <FormGroup>
            {permissions.map((permission) => (
              <FormControlLabel
                key={permission.id}
                control={
                  <Checkbox
                    checked={permissionIds.includes(permission.id)}
                    onChange={() =>
                      setPermissionIds((prev) => toggle(prev, permission.id))
                    }
                  />
                }
                label={
                  <span>
                    {catalogText(
                      t,
                      `admin.access.permissions.${permission.id}.name`,
                      permission.name,
                    )}
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block' }}
                    >
                      {catalogText(
                        t,
                        `admin.access.permissions.${permission.id}.description`,
                        permission.description,
                      )}
                    </Typography>
                  </span>
                }
              />
            ))}
          </FormGroup>

          <Typography variant="subtitle2">
            {t('admin.users.dialog.companies')}
          </Typography>
          {superAdminSelected ? (
            <Alert severity="info">
              {t('admin.users.dialog.superAdminCompanies')}
            </Alert>
          ) : companies.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              {t('admin.users.dialog.noCompanies')}
            </Typography>
          ) : (
            <Autocomplete
              multiple
              options={companies}
              value={selectedCompanies}
              onChange={(_event, next) => setSelectedCompanies(next)}
              getOptionLabel={(company) => company.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              filterSelectedOptions
              disableCloseOnSelect
              noOptionsText={t('admin.users.dialog.noCompaniesMatch')}
              renderTags={(value, getTagProps) =>
                value.map((company, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      size="small"
                      label={company.name}
                      {...tagProps}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('admin.users.dialog.linkedCompanies')}
                  placeholder={
                    selectedCompanies.length === 0
                      ? t('admin.users.dialog.searchCompanies')
                      : undefined
                  }
                  helperText={t('admin.users.dialog.linkedCompaniesHelp')}
                />
              )}
            />
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('admin.common.cancel')}</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={() => void submit()}>
          {creating
            ? t('admin.users.dialog.create')
            : t('admin.users.dialog.saveChanges')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
